import api from '@/services/Api';
import moment from 'moment';
import { successSwal, toastSwal } from '@/helper/swals';
import { ActionContext, Module } from 'vuex';
import { AxiosResponse } from 'axios';
import Beleg from '@/types/Beleg';
import {
    ZahlungserinnerungFilter,
    ZahlungserinnerungState,
    RootState,
} from '@/store/types';
import { DataResponse } from '@/types';
import PrintManager from '@/services/PrintManager';
import {LoadOffeneBelegeData} from '@/store/kassafuehrung';
import { saveAs } from 'file-saver';
export type LoadZahlungserinnerungenPayload = { filter: ZahlungserinnerungFilter };
export type OpenBriefPayload = { beleg_id: number };
export type PrintBriefPayload = { briefe: number[]; text: string | null, export: boolean, name: string };
export type PrintBriefePayload = { briefe: number[]; };
export type SendEmailPayload = { briefe: number[]; };
export type SendSmsPayload = { briefe: number[]; };
export type ZahlungserinnerungenOptions = {
    zeitraum: [string, string];
    zahlungsziel_tage: number | null;
    briefe : number[]
};
export type AlsGemanhtMarkierenPayload = {
    zeitraum: [string, string];
    zahlungsziel_tage: number | null;
    briefe : number[]
};
export type SetZahlungserinnerungenDialogs = { brief: boolean };
export type SetZahlungserinnerungenKarteiLoaders = { loadingId?: number | null; impfungen?: boolean };
export type SetZahlungserinnerungenBriefLoaders = { loadingId?: number | null; print?: boolean };
export type SetZahlungserinnerungenLoaders = {
    print?: boolean;
    email?: boolean;
    sms?: boolean;
    mahnung?: boolean;
    Zahlungserinnerungen?: boolean;
};

type Context = ActionContext<ZahlungserinnerungState, RootState>;

export const zahlungserinnerungen: Module<ZahlungserinnerungState, RootState> = {
    namespaced: true,
    state: {
        dialogs: {
            brief: false,
        },
        loaders: {
            brief: {
                loadingId: null,
                print: false,
                export: false,
            },
            print: false,
            email: false,
            sms: false,
            mahnung: false,
            offeneBelege: false,
        },
        belege: [],
        totalCount: 0,
        selectedItems: [],
        filter: {
            email: 'n',
            sms: 'n',
        },
        brief: new Beleg(),
        offeneZahlscheinBelege: [],
    },
    actions: {
        openBrief({ commit }: Context, payload: OpenBriefPayload): void {
            commit('setLoadersBrief', { loadingId: payload });

            api.post('zahlungserinnerungen/zahlungserinnerungsbrief/load', { payload })
                .then((response: AxiosResponse) => {
                    commit('setBrief', response.data.body);
                    commit('setDialogs', { brief: true });
                })
                .catch(console.error)
                .finally(() => commit('setLoadersBrief', { loadingId: null }));
        },
        printBrief({ commit, dispatch, rootState }: Context, payload: PrintBriefPayload): void {
            const isExport = payload.export;
            const patientName = payload.name;

            commit('setLoadersBrief', { [isExport ? 'export' : 'print']: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;
            const data = { impfstelle_id, ...payload };

            api.post('zahlungserinnerungen/zahlungserinnerungsbrief/index', data, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => {
                    if (isExport) {
                        const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                        saveAs(data, `Zahlungserinnerungsbrief_${patientName}_${date}.pdf`);
                    } else {
                        dispatch('globals/printDokument', { data }, { root: true });
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersBrief', { [isExport ? 'export' : 'print']: false }));
        },
        printBriefe({ commit, dispatch, rootState }: Context, payload: PrintBriefePayload): void {
            commit('setLoaders', { print: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;
            const data = { impfstelle_id, ...payload };
            const duplex = PrintManager.printer?.manager?.DuplexMode?.Simplex;

            api.post('zahlungserinnerungen/zahlungserinnerungsbrief/index', data, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => {
                    //commit('setSelectedItems', []);
                    dispatch('globals/printDokument', { data, duplex }, { root: true });
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { print: false }));
        },
        closeBrief({ commit }: Context): void {
            commit('setDialogs', { brief: false });
            commit('setBrief', null);
        },
        sendEmail({ commit, rootState }: Context, payload: SendEmailPayload): void {
            commit('setLoaders', { email: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('zahlungserinnerungen/zahlungserinnerungsbrief/email', {briefe: payload.briefe, impfstelle_id: impfstelle_id})
                .then((response: AxiosResponse) => {
                    //commit('setSelectedItems', []);

                    const sent = response.data.body;
                    const toSend = payload.briefe.length;
                    const success = sent === toSend;

                    toastSwal(
                        success ? 'success' : 'warning',
                        'Zahlungserinnerungen Emails',
                        `${sent} von ${toSend} gesendet`,
                        success ? 3000 : 6000);
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { email: false }));
        },
        loadOffeneZahlscheinBelege({ commit, rootState }: Context, payload: ZahlungserinnerungenOptions): void {
            commit('setLoadersOffeneBelege', true);

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('kassa/kassaabschluesse/offenezahlscheinbelege', { impfstelle_id, zahlungsziel_tage: payload.zahlungsziel_tage, zeitraum: payload.zeitraum})
                .then((response: AxiosResponse) => commit('setOffeneZahlscheinBelege', response.data))
                .catch(console.error)
                .finally(() => commit('setLoadersOffeneBelege', false));
        },
        alsGemahntMarkieren({ commit, rootState, dispatch }: Context, payload: AlsGemanhtMarkierenPayload):
            void {
            commit('setLoaders', { mahnung: true });

            api.post('zahlungserinnerungen/zahlungserinnerungen/gemahnt', { briefe: payload.briefe })
                .then(() => {
                    commit('setSelectedItems', []);
                    dispatch('loadOffeneZahlscheinBelege', payload);
                    successSwal('Zahlungserinnerungen', 'als gemahnt markiert');
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { mahnung: false }));
        },
        setDialogs({ commit }: Context, payload: SetZahlungserinnerungenDialogs): void {
            commit('setDialogs', payload);
        },
        setLoaders({ commit }: Context, payload: SetZahlungserinnerungenLoaders): void {
            commit('setDialogs', payload);
        },
        setBrief({ commit }: Context, payload: Beleg | null): void {
            commit('setBrief', payload);
        },
        setSelectedItems({ commit }: Context, payload: Beleg[]): void {
            commit('setSelectedItems', payload);
        },
    },
    mutations: {
        setTotalCount(state: ZahlungserinnerungState, totalCount: number): void {
            state.totalCount = totalCount;
        },
        setSelectedItems(state: ZahlungserinnerungState, items: Beleg[]): void {
            state.selectedItems = items;
        },
        setBrief(state: ZahlungserinnerungState, payload: Beleg | null): void {
            state.brief = payload;
        },
        setDialogs(state: ZahlungserinnerungState, payload: SetZahlungserinnerungenDialogs): void {
            state.dialogs = {
                ...state.dialogs,
                ...payload,
            };
        },
        setLoaders(state: ZahlungserinnerungState, payload?: SetZahlungserinnerungenLoaders): void {
            state.loaders = {
                ...state.loaders,
                ...payload,
            };
        },
        setLoadersBrief(state: ZahlungserinnerungState, payload: SetZahlungserinnerungenBriefLoaders): void {
            state.loaders.brief = {
                ...state.loaders.brief,
                ...payload,
            };
        },
        setLoadersOffeneBelege(state: ZahlungserinnerungState, loading: boolean): void {
            state.loaders.offeneBelege = loading;
        },
        setOffeneZahlscheinBelege(state: ZahlungserinnerungState, { body }: LoadOffeneBelegeData): void {
            state.offeneZahlscheinBelege = body.belege.map((data) => new Beleg(data));
        },
    },
};
